import { Children } from 'react';
import { Box, Divider, Drawer, IconButton, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

// The slide-out panels (quota editor, rule editor) share one structure: a header, alerts, then
// sections with small headings and dividers, and a footer pinned to the bottom. Spacing between
// sections and the heading style are set here, once.
const GUTTER = 3;

/** The panel itself: a scrolling body and a pinned footer. */
export const Panel = ({ busy, onClose, children, footer }) => (
  <Drawer anchor="right" open onClose={busy ? undefined : onClose}
    PaperProps={{ sx: { width: { xs: '100%', md: 720 }, display: 'flex', flexDirection: 'column' } }}>
    <Box sx={{ overflowY: 'auto', flex: 1, pb: 2 }}>{children}</Box>
    <Box component="footer" sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper', px: GUTTER, py: 1.5 }}>
      {footer}
    </Box>
  </Drawer>
);

/** The title, its status chips beside it, and the close button. */
export const PanelHeader = ({ title, chips, onClose, busy }) => (
  <Box sx={{ px: GUTTER, pt: 2.5, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
    <Typography variant="h5" component="h2" fontFamily="'Work Sans', sans-serif" fontWeight={700} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
      {title}
    </Typography>
    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>{chips}</Box>
    <IconButton onClick={onClose} aria-label="Close" disabled={busy} sx={{ ml: 'auto' }}><CloseRoundedIcon /></IconButton>
  </Box>
);

/** Banners directly under the header; nothing is rendered when there are none. */
export const PanelAlerts = ({ children }) => {
  const shown = Children.toArray(children);
  if (!shown.length) return null;
  return <Box sx={{ px: GUTTER, pb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>{shown}</Box>;
};

/** One section: a divider, a small heading, and its content. */
export const PanelSection = ({ title, children }) => (
  <Box component="section" aria-label={title}>
    <Divider />
    <Box sx={{ px: GUTTER, pt: 2, pb: 2.5 }}>
      <Typography variant="overline" component="h3" color="text.secondary"
        sx={{ display: 'block', fontWeight: 700, letterSpacing: 0.8, lineHeight: 1.6, mb: 1.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  </Box>
);

/** The plain-English summary, highlighted: what the conditions mean comes before how they're built. */
export const SummaryBox = ({ children }) => (
  <Box sx={(theme) => ({
    p: 2, mb: 2, borderRadius: 1.5, borderLeft: `4px solid ${theme.palette.primary.main}`,
    bgcolor: alpha(theme.palette.primary.main, 0.07),
  })}>
    {children}
  </Box>
);

/** The condition builder's own bordered area. */
export const BuilderArea = ({ children }) => (
  <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1.5 }}>{children}</Box>
);

/** Footer actions: secondary ones (Duplicate, Delete) on the left, Cancel and Save on the right. */
export const FooterActions = ({ secondary, primary }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
    {secondary}
    <Box sx={{ flex: 1 }} />
    {primary}
  </Box>
);
